import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { DAY_MILLISECONDS, toISOStringNoTZ } from 'cityads-v3/utils/dateUtils'
import { deepCompare } from 'cityads-v3/utils/arrayUtils'

import {
  setForm,
  saveForm,
  setError,
} from 'cityads-v3/actions/welcomeform'

import Button from '@mui/material/Button'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PickerFieldUISlotProps } from '@mui/x-date-pickers/internals'

import TextField from 'cityads-v3/components/TextField/TextField'
import ContactField, { Icons } from 'cityads-v3/components/TextField/ContactField'
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton'

import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined'
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'

import classes from './WelcomeForm.scss'

import {
  WEBMASTER_TYPE_PUBLISHER,
  WEBMASTER_TYPE_MEDIABUYER,
  WEBMASTER_TYPE_NETWORK,
} from 'cityads-v3/reducers/welcomeform'

import {
  formItemIconSx,
  MAX_DISPLAYED_SELECTED,
  menuProps
} from './WelcomeForm'
import { RootState } from 'cityads-v3/types/reducer'
import { WelcomeFormData } from 'cityads-v3/types/welcomeform'
import { ContactType } from 'cityads-v3/types/contactTypes'

type RequiredFieldsType = (webmaster_type: number) => { name: keyof WelcomeFormData, filter: boolean }[]
const REQUIRED_FIELDS: RequiredFieldsType = (webmaster_type: number) => [
  {
    name: 'name',
    filter: webmaster_type === WEBMASTER_TYPE_PUBLISHER || webmaster_type === WEBMASTER_TYPE_MEDIABUYER
  },
  {
    name: 'company',
    filter: webmaster_type === WEBMASTER_TYPE_NETWORK
  }
]

function checkSomeContacts({
  dispatch,
  form,
}: {
  dispatch: ReturnType<typeof useDispatch>
  form: WelcomeFormData
}) {
  const contactError = !Object.keys(form.contacts).some(id => form.contacts[id])

  if (contactError) {
    dispatch(setError({
      contactError
    }))
    return false
  }

  dispatch(setError({
    contactError: false
  }))

  return true
}

export function checkFields({
  dispatch,
  form
}: {
  dispatch: ReturnType<typeof useDispatch>
  form: WelcomeFormData
}) {
  const { webmaster_type } = form
  let allChecked = true

  REQUIRED_FIELDS(webmaster_type)
    .filter(item => item.filter || item.filter === undefined)
    .forEach(item => {
      if (!form[item.name]) {
        dispatch(setError({
          [item.name]: true
        }))
        allChecked = false
      } else if (form[item.name]) {
        dispatch(setError({
          [item.name]: false
        }))
      }
    })


  if (!checkSomeContacts({ dispatch, form })) {
    allChecked = false
  }

  return allChecked
}

type Step2Props = {
  inline?: boolean
  inlineClasses?: {
    root?: string
    formItems?: string
  }
}

export default function Step2({
  inline = false,
  inlineClasses,
}: Step2Props) {
  const { t } = useTranslation(['Welcome', 'main'])
  const dispatch = useDispatch()

  const form = useSelector((state: RootState) => state.welcomeform.formData)
  const errors = useSelector((state: RootState) => state.welcomeform.errors)
  const errorMessages = useSelector((state: RootState) => state.welcomeform.errorMessages)
  const contactTypes = useSelector((state: RootState) => state.contactTypes.contactTypes.map(item => state.contactTypes.contactTypesById[item]))
  const contactTypesById = useSelector((state: RootState) => state.contactTypes.contactTypesById)
  const isSavingForm = useSelector((state: RootState) => state.welcomeform.isSavingForm)

  const selectedContacts = Object.keys(form.contacts || {})
  const yesterday = new Date((new Date()).valueOf() - DAY_MILLISECONDS)
  const { name: errorName, company: errorCompany, contactError } = errors
  const { webmaster_type, birthday, contacts } = form

  const handleGoNext = useCallback(() => {
    if (checkFields({ dispatch, form })) {
      dispatch(saveForm('contacts'))
        .then(() => {
          dispatch(setForm({
            data: {},
            page: 3
          }))
        })

    }
  }, [dispatch, form])

  const handleGoPrev = useCallback(() => {
    dispatch(setForm({
      data: {},
      page: 1
    }))
  }, [dispatch])

  const checkField = useCallback((name: keyof WelcomeFormData, newValue: string | number | number[] | Record<string, string>) => {
    const newForm = {
      ...form,
      [name]: newValue
    }
    let isValid = false

    if ((typeof newValue === 'string' || Array.isArray(newValue)) && newValue.length > 0) {
      isValid = true
    } else if (newValue) {
      isValid = true
    }

    const newErrors = {
      ...errors,
      [name]: !isValid
    }

    if (!deepCompare(newErrors, errors)) {
      dispatch(setError(newErrors))
    }

    checkSomeContacts({
      dispatch,
      form: newForm
    })
  }, [dispatch, errors, form])

  const handleChangeSelectedContacts = useCallback(evt => {
    const newSelectedContacts = evt.target.value as ContactType['id'][]
    const contacts = newSelectedContacts.reduce((acc, name) => ({ ...acc, [name]: form.contacts[name] || '' }), {} as Record<string, string>)

    checkField('contacts', contacts)

    dispatch(setForm({
      data: {
        contacts,
      },
      page: 2
    }))
  }, [checkField, dispatch, form.contacts])

  const handleChangeForm = useCallback((name, newValue) => {
    checkField(name, newValue)

    dispatch(setForm({
      data: {
        [name]: newValue
      },
      page: 2
    }))
  }, [checkField, dispatch])

  const handleChangeContactField = useCallback((name, newValue) => {
    const formData = {
      ...form.contacts,
      [name]: newValue
    }
    handleChangeForm('contacts', formData)
  }, [form.contacts, handleChangeForm])

  return (
    <div className={classNames(inlineClasses?.root, classes.form, classes.form__step2)}>
      {!inline && (
        <div className={classes.form__title}>
          {t('step2')}
        </div>
      )}

      <div className={classNames(classes.form__items, inlineClasses?.formItems)}>

        {(webmaster_type === WEBMASTER_TYPE_PUBLISHER || webmaster_type === WEBMASTER_TYPE_MEDIABUYER) && ([
          <div key="name" className={classes.form__item}>
            <PermContactCalendarOutlinedIcon sx={formItemIconSx} />

            <TextField
              label={t('fields.name')}
              fullWidth={true}
              value={form.name || ''}
              onChange={evt => handleChangeForm('name', evt.target.value)}
              error={!!errorName}
              helperText={errorName ? getErrorHelper(errorMessages.name) || t('errorEmptyField') : undefined}
            />
          </div>,
          <div key="birthday" className={classes.form__item}>
            <DateRangeOutlinedIcon sx={formItemIconSx} />
            <DatePicker
              disableFuture
              label={t('fields.birthday')}
              openTo="year"
              maxDate={moment(yesterday)}
              onChange={date => {
                if (date) {
                  handleChangeForm('birthday', toISOStringNoTZ(date.toDate()))
                } else {
                  handleChangeForm('birthday', null)
                }
              }}
              defaultValue={birthday ? moment(birthday.split('T')[0]) : null}
              slotProps={{
                textField: {
                  clearable: true,
                  fullWidth: true,
                  error: !!errorMessages.birthday,
                  helperText: getErrorHelper(errorMessages.birthday)
                } as PickerFieldUISlotProps['textField'],
              }}
            />
          </div>,
        ])}

        {webmaster_type === WEBMASTER_TYPE_NETWORK && ([
          <div key="company" className={classes.form__item}>
            <PermContactCalendarOutlinedIcon sx={formItemIconSx} />

            <TextField
              label={t('fields.companyName')}
              fullWidth={true}
              onChange={evt => handleChangeForm('company', evt.target.value)}
              error={!!errorCompany}
              helperText={errorCompany ? getErrorHelper(errorMessages.company) || t('errorEmptyField') : undefined}
              value={form.company || ''}
            />
          </div>,
          <div key="contact" className={classes.form__item}>
            <PermContactCalendarOutlinedIcon sx={formItemIconSx} />

            <TextField
              label={t('fields.contact')}
              fullWidth={true}
              onChange={evt => handleChangeForm('name', evt.target.value)}
              error={!!errorName}
              helperText={errorName ? getErrorHelper(errorMessages.name) || t('errorEmptyField') : undefined}
              value={form.name || ''}
            />
          </div>
        ])}

        <div className={classes.form__item}>
          <PhoneOutlinedIcon sx={formItemIconSx} />

          <ContactField
            slotProps={{
              select: {
                multiple: true,
                ...menuProps,
                renderValue: (selected: number[]) => selected.slice(0, MAX_DISPLAYED_SELECTED - 1).map(item => (contactTypesById[item] || {}).name).join(', ')
              }
            }}
            error={errors.contactError && selectedContacts.length === 0}
            helperText={errors.contactError && selectedContacts.length === 0 ? t('contactsError') : undefined}
            label={t('fields.contacts')}
            value={selectedContacts}
            onChange={handleChangeSelectedContacts}
          />
        </div>

        {webmaster_type === WEBMASTER_TYPE_NETWORK && (
          <div key="domain" className={classes.form__item}>
            <PermContactCalendarOutlinedIcon sx={formItemIconSx} />

            <TextField
              label={t('fields.domain')}
              fullWidth={true}
              onChange={evt => handleChangeForm('domain', evt.target.value)}
              error={!!errorMessages.domain}
              helperText={getErrorHelper(errorMessages.domain)}
              value={form.domain || ''}
            />
          </div>
        )}

        {contactTypes.filter(item => contacts.hasOwnProperty(item.id)).map(item => (
          <div key={item.id} className={classes.form__item}>
            <Icons icon={item.id} sx={formItemIconSx} color="inherit" />

            <TextField
              label={item.name}
              fullWidth={true}
              onChange={evt => handleChangeContactField(item.id, evt.target.value)}
              error={!!contactError}
              helperText={contactError ? t('contactsError') : undefined}
              value={contacts[item.id] || ''}
            />
          </div>
        ))}

      </div>

      {!inline && (
        <div className={classes.buttons}>
          <Button onClick={handleGoPrev} color="info" variant="contained">{t('main:buttons.back')}</Button>
          <WaitingButton
            variant="contained"
            color="primary"
            onClick={handleGoNext}
            waiting={isSavingForm}
          >
            {t('continue')}
          </WaitingButton>
        </div>
      )}
    </div>
  )
}

function getErrorHelper(messages: string[]) {
  if (!messages) {
    return null
  }

  return messages.map((m, i) => (<React.Fragment key={i}>{m}<br /></React.Fragment>))
}
